import { ArrowRightIcon, CheckCircleIcon, ChevronDownIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

type WelcomeCardProps = {
  onOpenPenalModal: () => void;
  onGoToMarketVision?: () => void;
};

function WelcomeCard({ onOpenPenalModal, onGoToMarketVision }: WelcomeCardProps) {
  const [isWhyOpen, setIsWhyOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-md">
      {/* Hero gradient */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-rgpd-blue-dark to-rgpd-blue px-10 py-5">
        <div className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full bg-white/5" />
        <h1 className="mb-2 text-[22px] font-extrabold text-white">
          Votre Module Croissance
        </h1>
        <p className="text-[15px] leading-relaxed text-white/85">
          Développez votre CA en transaction, location et gestion locative, tout en restant{' '}
          <strong className="text-white">strictement conforme au RGPD</strong>.
        </p>
      </div>

      {/* Why bar */}
      <div className="border-y border-gray-200 bg-white px-10 py-3.5">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span className="text-[13px] font-bold text-gray-900">
            Pourquoi utiliser ce module ?
          </span>
          <span className="text-[13px] text-gray-900">
            Pour éviter le détournement de finalité
          </span>
          <button
            type="button"
            onClick={() => setIsWhyOpen((v) => !v)}
            className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-slate-900 transition-colors hover:bg-blue-100"
          >
            {isWhyOpen ? 'Fermer' : 'En savoir plus'}
            <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${isWhyOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {isWhyOpen && (
        <div className="border-b border-gray-200 bg-white px-10 py-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-[13px] leading-relaxed text-gray-900 shadow-sm">
            <strong className="mb-2 block text-sm text-rgpd-blue-dark">
              Le détournement de finalité, c&apos;est quoi ?
            </strong>
            Le fichier des copropriétaires a été constitué uniquement pour gérer la copropriété.
            Utiliser ce fichier pour proposer des services de transaction ou de gestion locative
            revient à détourner cette finalité, infraction pénale passible de{' '}
            <strong>5 ans d&apos;emprisonnement et de 300 000 € d&apos;amende</strong>.
          </div>
        </div>
      )}

      {/* Pillars */}
      <div className="bg-white px-10 py-6">
        <div className="flex gap-3.5">
          <div className="flex flex-1 items-start gap-2.5 rounded-lg border border-green-200 bg-green-50 p-3.5">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-600 text-white">
              <CheckCircleIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-gray-900">
                Avec le module CROISSANCE on construit
              </div>
              <div className="mt-1 text-[13px] leading-relaxed text-gray-900">
                Relation de confiance · Opportunités naturelles · Conformité garantie · Stratégie sur 12 mois encadrée juridiquement
              </div>
              {onGoToMarketVision && (
                <button
                  type="button"
                  onClick={onGoToMarketVision}
                  className="mt-2.5 inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1 text-[10px] font-bold text-white transition-colors hover:bg-green-700"
                >
                  <ArrowRightIcon className="h-3 w-3" />
                  Découvrir la vision de votre marché &rarr;
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-1 items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3.5">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white">
              <XMarkIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-gray-900">
                Avec le module CROISSANCE on évite le détournement de finalité
              </div>
              <div className="mt-1 text-[13px] leading-relaxed text-gray-900">
                Démarchage direct · Confusion information/commerce · Risque d&apos;amende pour détournement de finalité
              </div>
              <button
                type="button"
                onClick={onOpenPenalModal}
                className="mt-2.5 inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1 text-[10px] font-bold text-white transition-colors hover:bg-red-700"
              >
                <ExclamationTriangleIcon className="h-3 w-3" />
                5 ans de prison et 300 000 € d&apos;amende &rarr;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WelcomeCard;
